import React, { useState, useEffect, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import { NumericFormat } from 'react-number-format';
import DatePicker, { registerLocale } from 'react-datepicker';
import ptBR from 'date-fns/locale/pt-BR';
import 'react-datepicker/dist/react-datepicker.css';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import bootstrapPlugin from '@fullcalendar/bootstrap';
import ptBrLocale from '@fullcalendar/core/locales/pt-br';
import Swal from 'sweetalert2';
import appointmentsService from '../../services/appointments';
import patientsService from '../../services/patientService';

registerLocale('pt-BR', ptBR);

interface Patient {
  id: string;
  firstName: string;
  lastName: string;
  consultationValue?: number | string;
}

interface AppointmentForm {
  id?: string;
  personId: string;
  personName?: string; // Nome completo do paciente selecionado
  date: string; // formato dd/mm/yyyy
  time: string; // formato hh:mm
  durationMinutes: number;
  status: string;
  notes: string;
  color: string;
  consultationValue: number;
  recurrenceId?: string;
}

interface RecurringForm {
  personId: string;
  personName?: string; // Nome completo do paciente selecionado
  consultationValue?: number;
  startDate: string; // formato dd/mm/yyyy
  startTime: string; // formato hh:mm
  durationMinutes: number;
  frequency: string;
  notes: string;
  color: string;
}

const Appointments: React.FC = () => {
  const calendarRef = useRef<FullCalendar>(null);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]); // Para o filtro
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingRecurring, setIsSavingRecurring] = useState(false);
  
  // Estados para autocomplete de pacientes
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [patientSearchResults, setPatientSearchResults] = useState<Patient[]>([]);
  const [showPatientSuggestions, setShowPatientSuggestions] = useState(false);
  const [isSearchingPatients, setIsSearchingPatients] = useState(false);
  
  // Estados para autocomplete de pacientes (modal recorrente)
  const [recurringPatientSearchTerm, setRecurringPatientSearchTerm] = useState('');
  const [recurringPatientSearchResults, setRecurringPatientSearchResults] = useState<Patient[]>([]);
  const [showRecurringPatientSuggestions, setShowRecurringPatientSuggestions] = useState(false);
  const [isSearchingRecurringPatients, setIsSearchingRecurringPatients] = useState(false);
  
  const [appointmentForm, setAppointmentForm] = useState<AppointmentForm>({
    personId: '',
    personName: '',
    date: '',
    time: '',
    durationMinutes: 50,
    status: 'SCHEDULED',
    notes: '',
    color: '#007bff',
    consultationValue: 0
  });

  const [recurringForm, setRecurringForm] = useState<RecurringForm>({
    personId: '',
    personName: '',
    consultationValue: 0,
    startDate: '',
    startTime: '',
    durationMinutes: 50,
    frequency: 'WEEKLY',
    notes: '',
    color: '#007bff'
  });

  // controla se o usuário editou manualmente o valor da sessão
  const [valueEdited, setValueEdited] = useState(false);

  // controla o período atual do calendário para recarregar com as mesmas datas
  const [currentDateRange, setCurrentDateRange] = useState<{ start: string; end: string } | null>(null);

  // Debounce para busca de pacientes
  useEffect(() => {
    if (!patientSearchTerm || patientSearchTerm.length < 2) {
      setPatientSearchResults([]);
      setShowPatientSuggestions(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setIsSearchingPatients(true);
        const results = await patientsService.getForSelect({ search: patientSearchTerm });
        setPatientSearchResults(results);
        setShowPatientSuggestions(true);
      } catch (error) {
        console.error('Erro ao buscar pacientes:', error);
      } finally {
        setIsSearchingPatients(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [patientSearchTerm]);

  // Fechar sugestões ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.patient-autocomplete')) {
        setShowPatientSuggestions(false);
        setShowRecurringPatientSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounce para busca de pacientes (modal recorrente)
  useEffect(() => {
    if (!recurringPatientSearchTerm || recurringPatientSearchTerm.length < 2) {
      setRecurringPatientSearchResults([]);
      setShowRecurringPatientSuggestions(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setIsSearchingRecurringPatients(true);
        const results = await patientsService.getForSelect({ search: recurringPatientSearchTerm });
        setRecurringPatientSearchResults(results);
        setShowRecurringPatientSuggestions(true);
      } catch (error) {
        console.error('Erro ao buscar pacientes:', error);
      } finally {
        setIsSearchingRecurringPatients(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [recurringPatientSearchTerm]);

  const [filters, setFilters] = useState({
    patientId: '',
    status: ''
  });

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        const patientsData = await patientsService.getForSelect();
        setPatients(patientsData);
        
        // Os appointments serão carregados pelo handleDatesSet do calendário na primeira renderização
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Erro ao carregar dados iniciais'
        });
      } finally {
        setLoading(false);
      }
    };
    
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (patients.length > 0 && currentDateRange) {
      loadAppointments({ startDate: currentDateRange.start, endDate: currentDateRange.end });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Garantir que quando patients são carregados E já existe um dateRange, recarregue
  useEffect(() => {
    if (patients.length > 0 && currentDateRange && appointments.length === 0) {
      loadAppointments({ startDate: currentDateRange.start, endDate: currentDateRange.end });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patients, currentDateRange]);

  const loadAppointments = async (extraParams: any = {}) => {
    try {
      const params: any = {};
      if (filters.patientId) params.personId = filters.patientId;
      if (filters.status) params.status = filters.status;
      // merge extra params like start/end
      Object.assign(params, extraParams || {});
      
      const data = await appointmentsService.getAll(params);
      setAppointments(Array.isArray(data) ? data : data.data || []);
    } catch (error) {
      console.error('Erro ao carregar agendamentos:', error);
    }
  };

  // Função auxiliar para recarregar appointments usando o período atual do calendário
  const reloadAppointments = async () => {
    if (currentDateRange) {
      await loadAppointments({ startDate: currentDateRange.start, endDate: currentDateRange.end });
    } else {
      await loadAppointments();
    }
  };

  const handleDatesSet = (info: any) => {
    // API expects YYYY-MM-DD format, not full ISO timestamp
    const start = info.start.toISOString().split('T')[0];
    const end = info.end.toISOString().split('T')[0];
    setCurrentDateRange({ start, end });
    loadAppointments({ startDate: start, endDate: end });
  };

  // Função auxiliar para converter date e time para ISO string
  // Função auxiliar para converter ISO string para date e time separados
  const parseISOToDateTime = (isoString: string): { date: string; time: string } => {
    const dateObj = new Date(isoString);
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    
    return {
      date: `${day}/${month}/${year}`,
      time: `${hours}:${minutes}`
    };
  };

  // Converter string dd/mm/yyyy para objeto Date
  const parseDateString = (dateStr: string): Date | null => {
    if (!dateStr || dateStr.length !== 10) return null;
    const [day, month, year] = dateStr.split('/').map(Number);
    if (!day || !month || !year) return null;
    return new Date(year, month - 1, day);
  };

  // Converter objeto Date para string dd/mm/yyyy
  const formatDateToString = (date: Date | null): string => {
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleDateClick = (arg: any) => {
    // arg.dateStr contém a data no formato YYYY-MM-DD
    const [year, month, day] = arg.dateStr.split('-').map(Number);
    
    // Criar data no formato dd/mm/yyyy
    const formattedDate = `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
    
    // Pegar hora atual e adicionar 1 hora
    const now = new Date();
    now.setHours(now.getHours() + 1);
    now.setMinutes(0);
    const formattedTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    
    setAppointmentForm({
      personId: '',
      personName: '',
      date: formattedDate,
      time: formattedTime,
      durationMinutes: 50,
      status: 'SCHEDULED',
      notes: '',
      color: '#007bff',
      consultationValue: 0
    });
    
    // Limpar autocomplete
    setPatientSearchTerm('');
    setPatientSearchResults([]);
    setShowPatientSuggestions(false);
    setValueEdited(false);
    
    setIsEditing(false);
    setShowModal(true);
  };

  const handleEventClick = (arg: any) => {
    const appointment = appointments.find(app => app.id === arg.event.id);
    if (!appointment) return;

    // Log para debug
    console.log('=== DEBUG EVENT CLICK ===');
    console.log('Appointment do backend:', appointment);
    console.log('Event do FullCalendar:', {
      id: arg.event.id,
      title: arg.event.title,
      start: arg.event.start,
      end: arg.event.end,
      allDay: arg.event.allDay,
      extendedProps: arg.event.extendedProps
    });

    let date: string;
    let time: string;
    let durationMinutes = appointment.durationMinutes || 50;
    
    // Se for evento all-day, usar a data sem considerar hora
    if (arg.event.allDay) {
      // Para eventos all-day, usar a data do evento e calcular duração
      const eventDate = arg.event.start || new Date();
      const day = String(eventDate.getDate()).padStart(2, '0');
      const month = String(eventDate.getMonth() + 1).padStart(2, '0');
      const year = eventDate.getFullYear();
      
      date = `${day}/${month}/${year}`;
      time = '08:00'; // Horário padrão para eventos all-day
      
      // Se tem start_date e end_date, calcular duração real
      if (appointment.start_date && appointment.end_date) {
        const startDate = new Date(appointment.start_date);
        const endDate = new Date(appointment.end_date);
        durationMinutes = Math.round((endDate.getTime() - startDate.getTime()) / 60000);
      }
    } else if (arg.event.start) {
      // Evento com horário específico
      const eventDate = new Date(arg.event.start);
      // FullCalendar com timezone nomeado ("America/Sao_Paulo") usa "fake UTC": os valores
      // UTC do objeto Date representam o horário de parede de São Paulo. Por isso usamos
      // getUTC* para obter o horário correto, independente do timezone do navegador.
      const day = String(eventDate.getUTCDate()).padStart(2, '0');
      const month = String(eventDate.getUTCMonth() + 1).padStart(2, '0');
      const year = eventDate.getUTCFullYear();
      const hours = String(eventDate.getUTCHours()).padStart(2, '0');
      const minutes = String(eventDate.getUTCMinutes()).padStart(2, '0');
      
      date = `${day}/${month}/${year}`;
      time = `${hours}:${minutes}`;
    } else {
      // Fallback: tentar usar campos do backend
      if (appointment.date && appointment.time) {
        // Converter date de YYYY-MM-DD para dd/mm/yyyy
        const [year, month, day] = appointment.date.split('-');
        date = `${day}/${month}/${year}`;
        time = appointment.time;
      } else if (appointment.dateTime) {
        // Fallback final: converter dateTime para date e time
        const parsed = parseISOToDateTime(appointment.dateTime);
        date = parsed.date;
        time = parsed.time;
      } else {
        // Último fallback: data/hora vazia
        date = '';
        time = '';
      }
    }

    // Buscar nome do paciente
    const patientName = appointment.personName || '';

    setAppointmentForm({
      id: appointment.id,
      personId: appointment.personId,
      personName: patientName,
      date: date,
      time: time,
      durationMinutes: durationMinutes,
      status: appointment.status,
      notes: appointment.notes || '',
      color: appointment.color || '#007bff',
      consultationValue: appointment.consultationValue || 0,
      recurrenceId: appointment.recurrenceId
    });
    
    // Inicializar autocomplete para modo edição
    setPatientSearchTerm(patientName);
    setPatientSearchResults([]);
    setShowPatientSuggestions(false);
    setValueEdited(false);
    
    setIsEditing(true);
    setShowModal(true);
  };

  const handleEventDrop = async (info: any) => {
    try {
      const appointment = appointments.find(app => app.id === info.event.id);
      if (!appointment) return;

      // Extract new date/time using getUTC* (FullCalendar fake UTC for named timezone)
      const eventStart = info.event.start;
      const newDay = String(eventStart.getUTCDate()).padStart(2, '0');
      const newMonth = String(eventStart.getUTCMonth() + 1).padStart(2, '0');
      const newYear = eventStart.getUTCFullYear();
      const newHours = String(eventStart.getUTCHours()).padStart(2, '0');
      const newMinutes = String(eventStart.getUTCMinutes()).padStart(2, '0');
      const newDateFormatted = `${newYear}-${newMonth}-${newDay}`;
      const newTimeFormatted = `${newHours}:${newMinutes}`;
      const newDateTime = info.event.start.toISOString();

      if (appointment.recurrenceId) {
        const result = await Swal.fire({
          title: 'Ajustar recorrência?',
          text: 'Este agendamento faz parte de uma série recorrente. Deseja ajustar os agendamentos subsequentes para o novo dia/horário?',
          icon: 'question',
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: 'Sim, ajustar subsequentes',
          denyButtonText: 'Não, apenas este',
          cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
          Swal.fire({ title: 'Aguarde...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
          const afterDateISO = `${appointment.date}T${appointment.time}:00Z`;
          await appointmentsService.updateRecurrenceFields(
            appointment.recurrenceId,
            {
              date: newDateFormatted,
              time: newTimeFormatted,
              afterDate: afterDateISO
            }
          );
          Swal.fire({ icon: 'success', title: 'Sucesso!', text: 'Recorrência ajustada com sucesso!', timer: 2000, showConfirmButton: false });
        } else if (result.isDenied) {
          Swal.fire({ title: 'Aguarde...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
          const appointmentData = appointmentsService.buildAppointmentData({
            personId: appointment.personId,
            dateTime: newDateTime,
            durationMinutes: appointment.durationMinutes || 50,
            status: appointment.status,
            notes: appointment.notes || '',
            color: appointment.color,
            consultationValue: appointment.consultationValue || 0
          });
          await appointmentsService.update(appointment.id, appointmentData);
          Swal.fire({ icon: 'success', title: 'Sucesso!', text: 'Agendamento movido com sucesso!', timer: 2000, showConfirmButton: false });
        } else {
          info.revert();
          return;
        }
      } else {
        Swal.fire({ title: 'Aguarde...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
        const appointmentData = appointmentsService.buildAppointmentData({
          personId: appointment.personId,
          dateTime: newDateTime,
          durationMinutes: appointment.durationMinutes || 50,
          status: appointment.status,
          notes: appointment.notes || '',
          color: appointment.color,
          consultationValue: appointment.consultationValue || 0
        });
        await appointmentsService.update(appointment.id, appointmentData);
      }

      await reloadAppointments();
      if (calendarRef.current) {
        calendarRef.current.getApi().refetchEvents();
      }
    } catch (error: any) {
      console.error('Erro ao mover agendamento:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: error.message || 'Erro ao mover agendamento'
      });
      info.revert(); // Reverte a mudança visual se houver erro
    }
  };

  const handleEventResize = async (info: any) => {
    try {
      const appointment = appointments.find(app => app.id === info.event.id);
      if (!appointment) return;

      const start = info.event.start;
      const end = info.event.end;
      const durationMinutes = Math.round((end.getTime() - start.getTime()) / 60000);
      
      const appointmentData = appointmentsService.buildAppointmentData({
        personId: appointment.personId,
        dateTime: start.toISOString(),
        durationMinutes: durationMinutes,
        status: appointment.status,
        notes: appointment.notes || '',
        color: appointment.color,
        consultationValue: appointment.consultationValue || 0
      });

      await appointmentsService.update(appointment.id, appointmentData);
      await reloadAppointments();
    } catch (error: any) {
      console.error('Erro ao redimensionar agendamento:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: error.message || 'Erro ao redimensionar agendamento'
      });
      info.revert(); // Reverte a mudança visual se houver erro
    }
  };

  const handleSaveAppointment = async () => {
    try {
      // Validar paciente
      if (!appointmentForm.personId) {
        Swal.fire({
          icon: 'warning',
          title: 'Atenção',
          text: 'Selecione um paciente válido da lista'
        });
        return;
      }

      if (!appointmentForm.date || !appointmentForm.time) {
        Swal.fire({
          icon: 'warning',
          title: 'Atenção',
          text: 'Preencha todos os campos obrigatórios'
        });
        return;
      }

      setIsSaving(true);

      // Converter date de dd/mm/yyyy para YYYY-MM-DD
      const [day, month, year] = appointmentForm.date.split('/');
      const dateFormatted = `${year}-${month}-${day}`;

      const appointmentData = appointmentsService.buildAppointmentData({
        personId: appointmentForm.personId,
        date: dateFormatted,
        time: appointmentForm.time,
        durationMinutes: appointmentForm.durationMinutes,
        status: appointmentForm.status,
        notes: appointmentForm.notes,
        color: appointmentForm.color,
        consultationValue: appointmentForm.consultationValue
      });

  if (isEditing && appointmentForm.id) {
        // Se for parte de uma recorrência, perguntar se aplica a todos ou só ao selecionado
        if (appointmentForm.recurrenceId) {
          const result = await Swal.fire({
            title: 'Aplicar alterações à recorrência?',
            text: 'Este agendamento faz parte de uma série recorrente. Deseja aplicar as alterações a todos os agendamentos futuros (incluindo este) ou apenas a este?',
            icon: 'question',
            showCancelButton: true,
            showDenyButton: true,
            confirmButtonText: 'Sim, aplicar aos futuros',
            denyButtonText: 'Não, apenas este',
            cancelButtonText: 'Cancelar'
          });

          if (result.isConfirmed) {
            // Criar ISO string para afterDate (RFC3339 requer timezone)
            const afterDateISO = `${dateFormatted}T${appointmentForm.time}:00Z`;
            
            // Atualizar campos da recorrência para este e agendamentos futuros
            await appointmentsService.updateRecurrenceFields(
              appointmentForm.recurrenceId, 
              { 
                color: appointmentForm.color, 
                consultationValue: appointmentForm.consultationValue,
                notes: appointmentForm.notes,
                date: dateFormatted,
                time: appointmentForm.time,
                afterDate: afterDateISO
              }
            );
            Swal.fire({ icon: 'success', title: 'Sucesso!', text: 'Alterações aplicadas aos agendamentos futuros da série!', timer: 2000, showConfirmButton: false });
          } else if (result.isDenied) {
            // Atualizar apenas este agendamento
            await appointmentsService.update(appointmentForm.id, appointmentData);
            Swal.fire({ icon: 'success', title: 'Sucesso!', text: 'Agendamento atualizado com sucesso!', timer: 2000, showConfirmButton: false });
          } else {
            // Cancelou
            return;
          }
        } else {
          // Não é recorrente: atualizar normalmente
          await appointmentsService.update(appointmentForm.id, appointmentData);
          Swal.fire({ icon: 'success', title: 'Sucesso!', text: 'Agendamento atualizado com sucesso!', timer: 2000, showConfirmButton: false });
        }
      } else {
        const createResp = await appointmentsService.create(appointmentData);

        // Validar resposta do backend para garantir que o agendamento foi criado
        const createdId = createResp && (createResp.id || createResp._id || (createResp.data && (createResp.data.id || createResp.data._id)));
        if (!createdId) {
          console.error('Criação retornou sem id - possível falha de persistência:', createResp);
          Swal.fire({
            icon: 'warning',
            title: 'Atenção',
            text: 'O servidor respondeu com sucesso, mas o agendamento pode não ter sido gravado. Verifique o servidor ou os logs.'
          });
        } else {
          Swal.fire({
            icon: 'success',
            title: 'Sucesso!',
            text: 'Agendamento criado com sucesso!',
            timer: 2000,
            showConfirmButton: false
          });
        }

        // Recarregar intervalo próximo à data do agendamento para garantir que o evento apareça
        try {
          const refDateISO = `${dateFormatted}T${appointmentForm.time}:00`;
          const refDate = new Date(refDateISO);
          const start = new Date(refDate);
          const end = new Date(refDate);
          start.setDate(start.getDate() - 7);
          end.setDate(end.getDate() + 7);
          await loadAppointments({ startDate: start.toISOString(), endDate: end.toISOString() });
        } catch (e) {
          // fallback: recarrega normalmente
          await reloadAppointments();
        }
      }

      setShowModal(false);
      await reloadAppointments();
      
      // Forçar atualização do calendário
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        calendarApi.refetchEvents();
      }
    } catch (error: any) {
      console.error('Erro ao salvar agendamento:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: error.message || 'Erro ao salvar agendamento'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAppointment = async () => {
    if (!appointmentForm.id) return;
    
    try {
      // Se tem recurrenceId, perguntar se deseja excluir subsequentes
      if (appointmentForm.recurrenceId) {
        const result = await Swal.fire({
          title: 'Excluir agendamentos recorrentes?',
          text: 'Este agendamento faz parte de uma série recorrente. Deseja excluir os agendamentos subsequentes?',
          icon: 'warning',
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: 'Sim, excluir subsequentes',
          denyButtonText: 'Não, apenas este',
          cancelButtonText: 'Cancelar',
          confirmButtonColor: '#d33',
          denyButtonColor: '#3085d6',
          cancelButtonColor: '#aaa'
        });

        if (result.isConfirmed) {
          // Excluir este e todos os subsequentes
          // Converter date de dd/mm/yyyy para YYYY-MM-DD e criar ISO
          const [day, month, year] = appointmentForm.date.split('/');
          const afterDate = `${year}-${month}-${day}T${appointmentForm.time}:00Z`;
          await appointmentsService.deleteRecurrenceAfterDate(appointmentForm.recurrenceId, afterDate);
          Swal.fire({
            icon: 'success',
            title: 'Excluídos!',
            text: 'Agendamentos subsequentes excluídos com sucesso!',
            timer: 2000,
            showConfirmButton: false
          });
        } else if (result.isDenied) {
          // Excluir apenas este
          await appointmentsService.delete(appointmentForm.id);
          Swal.fire({
            icon: 'success',
            title: 'Excluído!',
            text: 'Agendamento excluído com sucesso!',
            timer: 2000,
            showConfirmButton: false
          });
        } else {
          // Cancelou
          return;
        }
      } else {
        // Não tem recurrenceId, confirmação simples
        const result = await Swal.fire({
          title: 'Confirmar exclusão',
          text: 'Deseja realmente excluir este agendamento?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          cancelButtonColor: '#3085d6',
          confirmButtonText: 'Sim, excluir!',
          cancelButtonText: 'Cancelar'
        });

        if (!result.isConfirmed) return;

        await appointmentsService.delete(appointmentForm.id);
        Swal.fire({
          icon: 'success',
          title: 'Excluído!',
          text: 'Agendamento excluído com sucesso!',
          timer: 2000,
          showConfirmButton: false
        });
      }

      setShowModal(false);
      await reloadAppointments();
      
      // Forçar atualização do calendário
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        calendarApi.refetchEvents();
      }
    } catch (error: any) {
      console.error('Erro ao excluir agendamento:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: error.message || 'Erro ao excluir agendamento'
      });
    }
  };

  const handleSaveRecurring = async () => {
    try {
      // Validar paciente
      if (!recurringForm.personId) {
        Swal.fire({
          icon: 'warning',
          title: 'Atenção',
          text: 'Selecione um paciente válido da lista'
        });
        return;
      }

      if (!recurringForm.startDate || !recurringForm.startTime) {
        Swal.fire({
          icon: 'warning',
          title: 'Atenção',
          text: 'Preencha todos os campos obrigatórios'
        });
        return;
      }

      setIsSavingRecurring(true);

      const recurringData = appointmentsService.buildRecurringData(recurringForm);
      await appointmentsService.createRecurring(recurringData);
      
      Swal.fire({
        icon: 'success',
        title: 'Sucesso!',
        text: 'Agendamentos recorrentes criados com sucesso!',
        timer: 2000,
        showConfirmButton: false
      });
      setShowRecurringModal(false);
      await reloadAppointments();
      
      // Forçar atualização do calendário
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        calendarApi.refetchEvents();
      }
    } catch (error: any) {
      console.error('Erro ao criar agendamentos recorrentes:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: error.message || 'Erro ao criar agendamentos recorrentes'
      });
    } finally {
      setIsSavingRecurring(false);
    }
  };

  const rawEvents = appointmentsService.formatForCalendar(appointments, patients);
  const calendarEvents = (Array.isArray(rawEvents) ? rawEvents.filter((event: any) => event !== null) : []) as any[];

  if (loading) {
    return (
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">Agenda de Atendimentos</h1>
              </div>
            </div>
          </div>
        </div>
        <section className="content">
          <div className="container-fluid">
            <div className="text-center">
              <div className="spinner-border" role="status">
                <span className="sr-only">Carregando...</span>
              </div>
              <p className="mt-2">Carregando agenda...</p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0">Agenda de Atendimentos</h1>
            </div>
          </div>
        </div>
      </div>

      <section className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-3">
              <div className="sticky-top mb-3">
                <div className="card">
                  <div className="card-header">
                    <h4 className="card-title">Criar Agendamento</h4>
                  </div>
                  <div className="card-body">
                    <button 
                      className="btn btn-primary btn-block mb-3"
                      onClick={() => {
                        // Pegar hora atual + 1 hora
                        const now = new Date();
                        now.setHours(now.getHours() + 1);
                        now.setMinutes(0);
                        const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
                        
                        setAppointmentForm({
                          personId: '',
                          personName: '',
                          date: '',
                          time: currentTime,
                          durationMinutes: 50,
                          status: 'SCHEDULED',
                          notes: '',
                          color: '#007bff',
                          consultationValue: 0
                        });
                        
                        // Limpar autocomplete
                        setPatientSearchTerm('');
                        setPatientSearchResults([]);
                        setShowPatientSuggestions(false);
                        setValueEdited(false);
                        
                        setIsEditing(false);
                        setShowModal(true);
                      }}
                    >
                      <i className="fas fa-plus"></i> Novo Agendamento
                    </button>
                    <button 
                      className="btn btn-info btn-block"
                      onClick={() => {
                        // Limpar modal recorrente
                        setRecurringForm({
                          personId: '',
                          personName: '',
                          consultationValue: 0,
                          startDate: '',
                          startTime: '',
                          durationMinutes: 50,
                          frequency: 'WEEKLY',
                          notes: '',
                          color: '#007bff'
                        });
                        
                        // Limpar autocomplete recorrente
                        setRecurringPatientSearchTerm('');
                        setRecurringPatientSearchResults([]);
                        setShowRecurringPatientSuggestions(false);
                        setValueEdited(false);
                        
                        setShowRecurringModal(true);
                      }}
                    >
                      <i className="fas fa-redo"></i> Agendamento Recorrente
                    </button>
                  </div>
                </div>
                
                <div className="card">
                  <div className="card-header">
                    <h4 className="card-title">Filtros</h4>
                  </div>
                  <div className="card-body">
                    <div className="form-group">
                      <label>Paciente</label>
                      <select 
                        className="form-control"
                        value={filters.patientId}
                        onChange={(e) => setFilters(prev => ({ ...prev, patientId: e.target.value }))}
                      >
                        <option value="">Todos os pacientes</option>
                        {patients.map(patient => (
                          <option key={patient.id} value={patient.id}>
                            {patient.firstName} {patient.lastName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Status</label>
                      <select 
                        className="form-control"
                        value={filters.status}
                        onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                      >
                        <option value="">Todos</option>
                        <option value="SCHEDULED">Agendado</option>
                        <option value="COMPLETED">Concluído</option>
                        <option value="CANCELLED">Cancelado</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="col-md-9">
              <div className="card card-primary">
                <div className="card-body p-0">
                  <FullCalendar
                    ref={calendarRef}
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, bootstrapPlugin]}
                    headerToolbar={{
                      left: 'prev,next today',
                      center: 'title',
                      right: 'dayGridMonth,timeGridWeek,timeGridDay'
                    }}
                    themeSystem="bootstrap"
                    initialView="dayGridMonth"
                    locale={ptBrLocale}
                    timeZone="America/Sao_Paulo"
                    editable={true}
                    selectable={true}
                    selectMirror={true}
                    dayMaxEvents={true}
                    weekends={true}
                    events={calendarEvents}
                    dateClick={handleDateClick}
                    datesSet={handleDatesSet}
                    eventClick={handleEventClick}
                    eventDrop={handleEventDrop}
                    eventResize={handleEventResize}
                    slotMinTime="07:00:00"
                    slotMaxTime="22:00:00"
                    eventDisplay="block"
                    eventTimeFormat={{
                        hour: '2-digit',
                        minute: '2-digit',
                        meridiem: false
                    }}
                    businessHours={{
                      daysOfWeek: [1, 2, 3, 4, 5],
                      startTime: '08:00',
                      endTime: '18:00'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modal Agendamento */}
      {showModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
    <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">
                  {isEditing ? 'Editar Agendamento' : 'Novo Agendamento'}
                </h4>
                <button 
                  type="button" 
                  className="close" 
                  onClick={() => setShowModal(false)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                <div className="form-group">
                  <label>Paciente *</label>
                  <div className="patient-autocomplete" style={{ position: 'relative' }}>
                    <input
                      type="text"
                      className={`form-control ${appointmentForm.personId ? 'is-valid' : ''}`}
                      value={appointmentForm.personName || patientSearchTerm}
                      onChange={(e) => {
                        const value = e.target.value;
                        setPatientSearchTerm(value);
                        setAppointmentForm(prev => ({ 
                          ...prev, 
                          personId: '', 
                          personName: value 
                        }));
                      }}
                      onFocus={() => {
                        if (patientSearchTerm.length >= 2) {
                          setShowPatientSuggestions(true);
                        }
                      }}
                      placeholder="Digite o nome do paciente..."
                      required
                      disabled={isEditing} // Não permite trocar paciente ao editar
                    />
                    {isSearchingPatients && (
                      <div className="position-absolute" style={{ right: '10px', top: '10px' }}>
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                          <span className="sr-only">Buscando...</span>
                        </div>
                      </div>
                    )}
                    {showPatientSuggestions && patientSearchResults.length > 0 && (
                      <div 
                        className="list-group" 
                        style={{ 
                          position: 'absolute', 
                          zIndex: 1000, 
                          width: '100%', 
                          maxHeight: '200px', 
                          overflowY: 'auto',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                        }}
                      >
                        {patientSearchResults.map(patient => (
                          <button
                            key={patient.id}
                            type="button"
                            className="list-group-item list-group-item-action"
                            onClick={() => {
                              const fullName = `${patient.firstName} ${patient.lastName}`;
                              setAppointmentForm(prev => ({ 
                                ...prev, 
                                personId: patient.id,
                                personName: fullName,
                                consultationValue: patient.consultationValue ? Number(patient.consultationValue) : prev.consultationValue
                              }));
                              setPatientSearchTerm(fullName);
                              setShowPatientSuggestions(false);
                              // Não resetar valueEdited - se usuário já editou, manter o valor editado
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-center">
                              <span>{patient.firstName} {patient.lastName}</span>
                              {patient.consultationValue && (
                                <small className="text-muted">
                                  R$ {Number(patient.consultationValue).toFixed(2).replace('.', ',')}
                                </small>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {!appointmentForm.personId && patientSearchTerm.length >= 2 && (
                    <small className="form-text text-danger">
                      Selecione um paciente válido da lista
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label>Valor da Sessão *</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text">R$</span>
                    </div>
                    <NumericFormat
                      className="form-control"
                      value={appointmentForm.consultationValue}
                      thousandSeparator='.'
                      decimalSeparator=','
                      decimalScale={2}
                      fixedDecimalScale
                      allowNegative={false}
                      onValueChange={(values: any) => {
                        const newValue = values.floatValue !== undefined ? values.floatValue : 0;
                        setAppointmentForm(prev => ({ ...prev, consultationValue: newValue }));
                        setValueEdited(true);
                      }}
                      placeholder="0,00"
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-8">
                    <div className="form-group">
                      <label>Data *</label>
                      <DatePicker
                        selected={parseDateString(appointmentForm.date)}
                        onChange={(date: Date | null) => {
                          setAppointmentForm(prev => ({ ...prev, date: formatDateToString(date) }));
                        }}
                        dateFormat="dd/MM/yyyy"
                        locale="pt-BR"
                        className="form-control"
                        wrapperClassName="d-block"
                        placeholderText="dd/mm/aaaa"
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Hora *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={appointmentForm.time}
                        onChange={(e) => {
                          let value = e.target.value.replace(/\D/g, '');
                          if (value.length >= 2) {
                            value = value.substring(0, 2) + ':' + value.substring(2, 4);
                          }
                          setAppointmentForm(prev => ({ ...prev, time: value }));
                        }}
                        placeholder="hh:mm"
                        maxLength={5}
                        required
                      />
                    </div>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Duração (minutos) *</label>
                  <input 
                    type="number" 
                    className="form-control"
                    value={appointmentForm.durationMinutes}
                    onChange={(e) => setAppointmentForm(prev => ({ ...prev, durationMinutes: parseInt(e.target.value) }))}
                    min="15" 
                    step="5" 
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Status</label>
                  <select 
                    className="form-control"
                    value={appointmentForm.status}
                    onChange={(e) => setAppointmentForm(prev => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="SCHEDULED">Agendado</option>
                    <option value="COMPLETED">Concluído</option>
                    <option value="CANCELLED">Cancelado</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Cor do Agendamento</label>
                  <div style={{ marginTop: '10px' }}>
                    {[
                      { color: '#007bff', class: 'text-primary' },
                      { color: '#ffc107', class: 'text-warning' },
                      { color: '#28a745', class: 'text-success' },
                      { color: '#dc3545', class: 'text-danger' },
                      { color: '#6c757d', class: 'text-secondary' },
                      { color: '#17a2b8', class: 'text-info' },
                      { color: '#6f42c1', style: { color: '#6f42c1' } },
                      { color: '#fd7e14', style: { color: '#fd7e14' } }
                    ].map((item) => (
                      <button
                        key={item.color}
                        type="button"
                        className="btn btn-link p-0 mr-2"
                        onClick={() => setAppointmentForm(prev => ({ ...prev, color: item.color }))}
                        style={{ textDecoration: 'none' }}
                      >
                        <i 
                          className={`fas ${appointmentForm.color === item.color ? 'fa-check-square' : 'fa-square'} fa-2x ${item.class || ''}`}
                          style={item.style}
                        ></i>
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Observações</label>
                  <textarea 
                    className="form-control"
                    rows={3}
                    value={appointmentForm.notes}
                    onChange={(e) => setAppointmentForm(prev => ({ ...prev, notes: e.target.value }))}
                  />
                </div>
              </div>
              <div className="modal-footer">
                {isEditing && (
                  <button 
                    type="button" 
                    className="btn btn-danger"
                    onClick={handleDeleteAppointment}
                    disabled={isSaving}
                  >
                    <i className="fas fa-trash"></i> Excluir
                  </button>
                )}
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowModal(false)}
                  disabled={isSaving}
                >
                  Cancelar
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={handleSaveAppointment}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <><span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span> Salvando...</>
                  ) : (
                    <><i className="fas fa-save"></i> Salvar</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Agendamento Recorrente */}
      {showRecurringModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">Criar Agendamentos Recorrentes</h4>
                <button 
                  type="button" 
                  className="close" 
                  onClick={() => setShowRecurringModal(false)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Paciente *</label>
                      <div className="patient-autocomplete" style={{ position: 'relative' }}>
                        <input
                          type="text"
                          className={`form-control ${recurringForm.personId ? 'is-valid' : ''}`}
                          value={recurringForm.personName || recurringPatientSearchTerm}
                          onChange={(e) => {
                            const value = e.target.value;
                            setRecurringPatientSearchTerm(value);
                            setRecurringForm(prev => ({ 
                              ...prev, 
                              personId: '', 
                              personName: value 
                            }));
                          }}
                          onFocus={() => {
                            if (recurringPatientSearchTerm.length >= 2) {
                              setShowRecurringPatientSuggestions(true);
                            }
                          }}
                          placeholder="Digite o nome do paciente..."
                          required
                        />
                        {isSearchingRecurringPatients && (
                          <div className="position-absolute" style={{ right: '10px', top: '10px' }}>
                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                              <span className="sr-only">Buscando...</span>
                            </div>
                          </div>
                        )}
                        {showRecurringPatientSuggestions && recurringPatientSearchResults.length > 0 && (
                          <div 
                            className="list-group" 
                            style={{ 
                              position: 'absolute', 
                              zIndex: 1000, 
                              width: '100%', 
                              maxHeight: '200px', 
                              overflowY: 'auto',
                              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                            }}
                          >
                            {recurringPatientSearchResults.map(patient => (
                              <button
                                key={patient.id}
                                type="button"
                                className="list-group-item list-group-item-action"
                                onClick={() => {
                                  const fullName = `${patient.firstName} ${patient.lastName}`;
                                  setRecurringForm(prev => ({ 
                                    ...prev, 
                                    personId: patient.id,
                                    personName: fullName,
                                    consultationValue: patient.consultationValue ? Number(patient.consultationValue) : prev.consultationValue
                                  }));
                                  setRecurringPatientSearchTerm(fullName);
                                  setShowRecurringPatientSuggestions(false);
                                  // Não resetar valueEdited - se usuário já editou, manter o valor editado
                                }}
                              >
                                <div className="d-flex justify-content-between align-items-center">
                                  <span>{patient.firstName} {patient.lastName}</span>
                                  {patient.consultationValue && (
                                    <small className="text-muted">
                                      R$ {Number(patient.consultationValue).toFixed(2).replace('.', ',')}
                                    </small>
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {!recurringForm.personId && recurringPatientSearchTerm.length >= 2 && (
                        <small className="form-text text-danger">
                          Selecione um paciente válido da lista
                        </small>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Valor da Sessão *</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text">R$</span>
                        </div>
                        <NumericFormat
                          className="form-control"
                          value={recurringForm.consultationValue}
                          thousandSeparator='.'
                          decimalSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          allowNegative={false}
                          onValueChange={(values: any) => {
                            const newValue = values.floatValue !== undefined ? values.floatValue : 0;
                            setRecurringForm(prev => ({ ...prev, consultationValue: newValue }));
                            setValueEdited(true);
                          }}
                          placeholder="0,00"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Duração (minutos) *</label>
                      <input 
                        type="number" 
                        className="form-control"
                        value={recurringForm.durationMinutes}
                        onChange={(e) => setRecurringForm(prev => ({ ...prev, durationMinutes: parseInt(e.target.value) }))}
                        min="15" 
                        step="15" 
                        required
                      />
                    </div>
                  </div>
                </div>
                
                <div className="row">
                  <div className="col-md-8">
                    <div className="form-group">
                      <label>Data de Início *</label>
                      <DatePicker
                        selected={parseDateString(recurringForm.startDate)}
                        onChange={(date: Date | null) => {
                          setRecurringForm(prev => ({ ...prev, startDate: formatDateToString(date) }));
                        }}
                        dateFormat="dd/MM/yyyy"
                        locale="pt-BR"
                        className="form-control"
                        wrapperClassName="d-block"
                        placeholderText="dd/mm/yyyy"
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Hora de Início *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={recurringForm.startTime}
                        onChange={(e) => {
                          let value = e.target.value.replace(/\D/g, '');
                          if (value.length > 4) value = value.slice(0, 4);
                          if (value.length >= 3) {
                            value = value.slice(0, 2) + ':' + value.slice(2);
                          }
                          setRecurringForm(prev => ({ ...prev, startTime: value }));
                        }}
                        placeholder="hh:mm"
                        required
                        maxLength={5}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Frequência *</label>
                  <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                    <select 
                      className="form-control"
                      value={recurringForm.frequency}
                      onChange={(e) => setRecurringForm(prev => ({ ...prev, frequency: e.target.value }))}
                      required
                    >
                      <option value="DAILY">Diário (todos os dias)</option>
                      <option value="WEEKLY">Semanal (toda semana)</option>
                      <option value="BIWEEKLY">Quinzenal (a cada 2 semanas)</option>
                      <option value="MONTHLY">Mensal (todo mês)</option>
                    </select>
                    <span className="badge badge-success" style={{ whiteSpace: 'nowrap', fontSize: '0.85rem', padding: '6px 10px' }}>
                      <i className="fas fa-infinity"></i> Permanente
                    </span>
                  </div>
                  <small className="form-text text-muted">Recorrência sem data de término — permanente.</small>
                </div>
                
                <div className="form-group">
                  <label>Cor do Agendamento</label>
                  <div style={{ marginTop: '10px' }}>
                    {[
                      { color: '#007bff', class: 'text-primary' },
                      { color: '#ffc107', class: 'text-warning' },
                      { color: '#28a745', class: 'text-success' },
                      { color: '#dc3545', class: 'text-danger' },
                      { color: '#6c757d', class: 'text-secondary' },
                      { color: '#17a2b8', class: 'text-info' },
                      { color: '#6f42c1', style: { color: '#6f42c1' } },
                      { color: '#fd7e14', style: { color: '#fd7e14' } }
                    ].map((item) => (
                      <button
                        key={item.color}
                        type="button"
                        className="btn btn-link p-0 mr-2"
                        onClick={() => setRecurringForm(prev => ({ ...prev, color: item.color }))}
                        style={{ textDecoration: 'none' }}
                      >
                        <i 
                          className={`fas ${recurringForm.color === item.color ? 'fa-check-square' : 'fa-square'} fa-2x ${item.class || ''}`}
                          style={item.style}
                        ></i>
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Observações</label>
                  <textarea 
                    className="form-control"
                    rows={3}
                    value={recurringForm.notes}
                    onChange={(e) => setRecurringForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Observações para todos os agendamentos..."
                  />
                </div>
                
                <div className="alert alert-info">
                  <i className="fas fa-info-circle"></i> 
                  <strong>Atenção:</strong> Os agendamentos serão criados automaticamente de acordo com a frequência selecionada, sem data de término. Para encerrar a recorrência, exclua os agendamentos futuros pela opção de excluir.
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowRecurringModal(false)}
                  disabled={isSavingRecurring}
                >
                  Cancelar
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={handleSaveRecurring}
                  disabled={isSavingRecurring}
                >
                  {isSavingRecurring ? (
                    <><span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span> Criando...</>
                  ) : (
                    <><i className="fas fa-calendar-check"></i> Criar Agendamentos</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Appointments;